// Getting local files into the uploader — without a "local pane". Photos and folders
// arrive by drag-and-drop, the "Add photos" / "Add folder" pickers, or a remembered
// folder (resume). Each becomes { file, rel }: `rel` is the path relative to what was
// added — the bare name for loose files, "Card 1/DCIM/100CANON/IMG_0001.JPG" for a
// folder — so a dropped folder keeps its structure under the destination, exactly like
// an FTP client. `planUpload` then decides what actually uploads.
import type { DirHandle, FileHandle } from "./useLocalFs";
import { isImage, isVideo } from "./useLocalFs";

export interface IntakeFile {
  file: File;
  /** Path relative to what was added (name for a loose file; "Folder/…/name" for a folder). */
  rel: string;
}

/** What was added, for the summary and for resume. */
export interface IntakeBatch {
  files: IntakeFile[];
  /** Top-level folder name when a single folder was added (e.g. "Card 1"). */
  rootName?: string;
  /** File System Access handle of that folder (Chromium) — lets a resume re-open it in one click. */
  handle?: DirHandle;
}

const skipName = (name: string) => name.startsWith(".") || name === "Thumbs.db" || name === "desktop.ini";

/** Files from an <input type=file> (multiple, or webkitdirectory for a folder). */
export const fromFileList = (list: FileList | File[] | null): IntakeBatch => {
  const files: IntakeFile[] = [];
  for (const f of Array.from(list || [])) {
    if (skipName(f.name)) continue;
    const rel = (f as File & { webkitRelativePath?: string }).webkitRelativePath || f.name;
    files.push({ file: f, rel });
  }
  const roots = new Set(files.map((f) => (f.rel.includes("/") ? f.rel.split("/")[0] : "")));
  const rootName = roots.size === 1 ? [...roots][0] || undefined : undefined;
  return { files, rootName };
};

/** Walk a folder handle (File System Access) — used for Chromium picks and resume. Lists the
 *  tree first, then opens the files a few dozen at a time (one getFile() per file is the slow
 *  part of a 100k-photo folder). Unreadable files are skipped. */
export const fromDirHandle = async (dir: DirHandle, base = dir.name): Promise<IntakeFile[]> => {
  const handles: Array<{ h: FileHandle; rel: string }> = [];
  const walk = async (d: DirHandle, prefix: string) => {
    for await (const h of d.values()) {
      if (skipName(h.name)) continue;
      if (h.kind === "directory") await walk(h as DirHandle, `${prefix}/${h.name}`);
      else handles.push({ h: h as FileHandle, rel: `${prefix}/${h.name}` });
    }
  };
  await walk(dir, base);
  const out: Array<IntakeFile | null> = new Array(handles.length).fill(null);
  let next = 0;
  const worker = async () => {
    while (next < handles.length) {
      const i = next++;
      try {
        out[i] = { file: await handles[i].h.getFile(), rel: handles[i].rel };
      } catch {
        out[i] = null;
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(32, handles.length) }, worker));
  return out.filter((x): x is IntakeFile => !!x);
};

// --- drag-and-drop ---------------------------------------------------------------
interface FsEntry {
  isFile: boolean;
  isDirectory: boolean;
  name: string;
  fullPath: string;
  file?: (ok: (f: File) => void, err: (e: unknown) => void) => void;
  createReader?: () => { readEntries: (ok: (e: FsEntry[]) => void, err: (e: unknown) => void) => void };
}

const entryFile = (e: FsEntry) => new Promise<File>((ok, err) => e.file!(ok, err));
const readAll = async (e: FsEntry): Promise<FsEntry[]> => {
  const reader = e.createReader!();
  const all: FsEntry[] = [];
  // readEntries returns at most ~100 entries per call — keep reading until it returns none.
  for (;;) {
    const batch = await new Promise<FsEntry[]>((ok, err) => reader.readEntries(ok, err));
    if (!batch.length) return all;
    all.push(...batch);
  }
};

/**
 * Files from a drop. MUST be called synchronously from the drop handler: DataTransfer
 * items are only readable during the event, so every entry/handle is grabbed up front and
 * only the reading is async. Folders are walked recursively (webkitGetAsEntry, all modern
 * browsers); in Chromium a single dropped folder's File System Access handle is kept too,
 * for one-click resume.
 */
export const fromDataTransfer = (dt: DataTransfer): Promise<IntakeBatch> => {
  const items = Array.from(dt.items || []).filter((it) => it.kind === "file");
  const entries = items.map((it) => (it as DataTransferItem & { webkitGetAsEntry?: () => FsEntry | null }).webkitGetAsEntry?.() ?? null);
  const handlePromises = items.map((it) => {
    const get = (it as DataTransferItem & { getAsFileSystemHandle?: () => Promise<unknown> }).getAsFileSystemHandle;
    return get ? get.call(it).catch(() => null) : Promise.resolve(null);
  });
  const looseFiles = entries.every((e) => !e) ? Array.from(dt.files || []) : [];

  return (async () => {
    if (looseFiles.length) return fromFileList(looseFiles);
    const files: IntakeFile[] = [];
    const walk = async (e: FsEntry, prefix: string): Promise<void> => {
      if (skipName(e.name)) return;
      const rel = prefix ? `${prefix}/${e.name}` : e.name;
      if (e.isFile) files.push({ file: await entryFile(e), rel });
      else if (e.isDirectory) for (const c of await readAll(e)) await walk(c, rel);
    };
    for (const e of entries) if (e) await walk(e, "");
    const dirs = entries.filter((e) => e?.isDirectory);
    const onlyOneFolder = dirs.length === 1 && entries.length === 1;
    let handle: DirHandle | undefined;
    if (onlyOneFolder) {
      const h = (await handlePromises[0]) as DirHandle | null;
      if (h && h.kind === "directory") handle = h;
    }
    return { files, rootName: onlyOneFolder ? dirs[0]!.name : undefined, handle };
  })();
};

// --- planning -----------------------------------------------------------------------
/** Join a destination subfolder ("DD/100EOS1D" or "") and a relative path. */
export const joinKey = (sub: string, rel: string): string => {
  const s = sub.replace(/^\/+|\/+$/g, "");
  return s ? `${s}/${rel}` : rel;
};

export const uploadableName = (name: string, videoAllowed: boolean): boolean =>
  /\.jpe?g$/i.test(name) || (videoAllowed && isVideo(name));

export interface UploadPlan {
  /** Files to upload with their key (path inside the collection). */
  upload: Array<{ file: File; key: string; isVideo: boolean }>;
  /** Already in the destination (same path) — skipped unless the user chooses to replace them. */
  existing: Array<{ file: File; key: string; isVideo: boolean }>;
  /** Not accepted here (non-JPEG images, videos without the plan, other files). */
  unsupported: number;
  /** The same path added twice in one go (overlapping drops) — uploaded once. */
  duplicates: number;
  bytes: number;
  existingBytes: number;
  /** Top-level folders this creates/extends under the destination (for the summary). */
  folders: string[];
  /** Preview-first: live from a quick copy, still owed the original (see splitPendingOriginals). */
  originals?: Array<{ file: File; key: string; isVideo: boolean }>;
  originalsBytes?: number;
}

/**
 * Decide what uploads: destination `sub` + each file's relative path is its key; files
 * whose key is already in the destination (`existingKeys`, from the server listing — null
 * if that couldn't be checked) are held back as "already uploaded". O(files).
 */
export const planUpload = (
  files: IntakeFile[],
  { sub, existingKeys, videoAllowed }: { sub: string; existingKeys: Set<string> | null; videoAllowed: boolean },
): UploadPlan => {
  const plan: UploadPlan = { upload: [], existing: [], unsupported: 0, duplicates: 0, bytes: 0, existingBytes: 0, folders: [] };
  const seen = new Set<string>();
  const folders = new Set<string>();
  for (const { file, rel } of files) {
    const name = rel.split("/").pop() || file.name;
    if (!uploadableName(name, videoAllowed)) {
      if (isImage(name) || isVideo(name) || name.includes(".")) plan.unsupported += 1;
      continue;
    }
    const key = joinKey(sub, rel);
    if (seen.has(key)) { plan.duplicates += 1; continue; }
    seen.add(key);
    if (rel.includes("/")) folders.add(rel.split("/")[0]);
    const item = { file, key, isVideo: isVideo(name) };
    if (existingKeys?.has(key)) { plan.existing.push(item); plan.existingBytes += file.size; }
    else { plan.upload.push(item); plan.bytes += file.size; }
  }
  plan.folders = [...folders].sort();
  return plan;
};

// --- preview-first: originals still owed -------------------------------------------------
/**
 * Pull the files whose key is still waiting for its original (preview-first: the photo went
 * live from a quick copy) out of the plan. Those keys DO exist (as copies), so planUpload
 * files them under "already uploaded"; they go into `originals` instead, to upload untouched
 * into the same keys. Keys are matched wherever they landed (upload too, in case the existing
 * check failed). Returns a new plan; the input is untouched.
 */
export const splitPendingOriginals = (plan: UploadPlan, pendingKeys: Set<string> | null | undefined): UploadPlan => {
  if (!pendingKeys?.size) return plan;
  const originals: UploadPlan["upload"] = [];
  const keep = (items: UploadPlan["upload"]) =>
    items.filter((x) => {
      if (!x.isVideo && pendingKeys.has(x.key)) { originals.push(x); return false; }
      return true;
    });
  const upload = keep(plan.upload);
  const existing = keep(plan.existing);
  if (!originals.length) return plan;
  const sum = (items: UploadPlan["upload"]) => items.reduce((n, x) => n + x.file.size, 0);
  return { ...plan, upload, existing, bytes: sum(upload), existingBytes: sum(existing), originals, originalsBytes: sum(originals) };
};

// --- the same photos already here under another folder --------------------------------------
// A collection keys each photo by its path inside it, so adding the same photos again from another folder
// (loose files first, then the card folder, then a subfolder of it…) makes NEW photos: duplicates in the
// gallery, and a buyer of the first upload's photo never gets its original (its key is never sent again).
// The intake catches that before anything uploads.

const fileNameOf = (key: string): string => key.split("/").pop() || key;

/** A new-path photo whose file name is already in the collection under another path. */
export interface ElsewhereMatch {
  file: File;
  key: string;
  isVideo: boolean;
  /** The one existing photo with that name (its path inside the collection). */
  existingKey: string;
}

export interface Elsewhere {
  /** Name in the collection exactly once and in this batch exactly once: almost certainly that photo. */
  matches: ElsewhereMatch[];
  /** Name shared with several photos here (or several files in this batch): can't tell which one it is. */
  ambiguous: UploadPlan["upload"];
}

/**
 * Photos this batch would upload under a NEW path (`plan.upload`) whose file name is already in the
 * collection (`existingKeys`, paths inside it) under a different path. Photos only. O(files + keys).
 */
export const findElsewhere = (plan: UploadPlan, existingKeys: Iterable<string> | null | undefined): Elsewhere => {
  const out: Elsewhere = { matches: [], ambiguous: [] };
  if (!existingKeys) return out;
  const byName = new Map<string, string[]>();
  for (const k of existingKeys) {
    const name = fileNameOf(k);
    const list = byName.get(name);
    if (list) list.push(k);
    else byName.set(name, [k]);
  }
  if (!byName.size) return out;
  const inBatch = new Map<string, number>();
  for (const x of [...plan.upload, ...plan.existing]) {
    if (!x.isVideo) inBatch.set(fileNameOf(x.key), (inBatch.get(fileNameOf(x.key)) ?? 0) + 1);
  }
  for (const x of plan.upload) {
    if (x.isVideo) continue;
    const name = fileNameOf(x.key);
    const there = (byName.get(name) ?? []).filter((k) => k !== x.key);
    if (!there.length) continue;
    if (there.length === 1 && inBatch.get(name) === 1) out.matches.push({ ...x, existingKey: there[0] });
    else out.ambiguous.push(x);
  }
  return out;
};

/** What to do with them: send each to the existing photo it matches ("existing" — the default, no duplicates,
 *  owed originals land where buyers bought them), or upload them again as new photos ("duplicate"). */
export type ElsewhereChoice = "existing" | "duplicate";

/**
 * Apply the choice to the plan (before splitPendingOriginals). "existing": matched photos take the existing
 * photo's key (so they're filed as already uploaded — and splitPendingOriginals turns the ones still owed their
 * original into originals for THAT photo); ambiguous ones are left out rather than duplicated. "duplicate": the
 * plan as it was. Returns a new plan; the input is untouched.
 */
export const applyElsewhere = (plan: UploadPlan, elsewhere: Elsewhere, choice: ElsewhereChoice): UploadPlan => {
  if (choice === "duplicate" || (!elsewhere.matches.length && !elsewhere.ambiguous.length)) return plan;
  const moved = new Set<File>([...elsewhere.matches.map((m) => m.file), ...elsewhere.ambiguous.map((x) => x.file)]);
  const upload = plan.upload.filter((x) => !moved.has(x.file));
  const taken = new Set(plan.existing.map((x) => x.key));
  const remapped = elsewhere.matches
    .filter((m) => !taken.has(m.existingKey))
    .map(({ file, isVideo, existingKey }) => ({ file, isVideo, key: existingKey }));
  const existing = [...plan.existing, ...remapped];
  const sum = (items: UploadPlan["upload"]) => items.reduce((n, x) => n + x.file.size, 0);
  // Only the folders something still uploads into.
  const folders = plan.folders.filter((f) => upload.some((x) => x.key === f || x.key.startsWith(`${f}/`) || x.key.includes(`/${f}/`)));
  return { ...plan, upload, existing, folders, bytes: sum(upload), existingBytes: sum(existing) };
};

/** What the upload summary says about them. */
export interface ElsewhereSummary {
  /** Photos that match one existing photo by name. */
  matches: number;
  /** Photos whose name several photos here share. */
  ambiguous: number;
  /** Folders (inside the collection; "" = its top level) the matched photos already are in — the first few. */
  where: string[];
  choice: ElsewhereChoice;
}

export const elsewhereSummary = (e: Elsewhere, choice: ElsewhereChoice): ElsewhereSummary | undefined => {
  if (!e.matches.length && !e.ambiguous.length) return undefined;
  const dirs = new Set<string>();
  for (const m of e.matches) {
    const i = m.existingKey.lastIndexOf("/");
    dirs.add(i < 0 ? "" : m.existingKey.slice(0, i));
    if (dirs.size >= 3) break;
  }
  return { matches: e.matches.length, ambiguous: e.ambiguous.length, where: [...dirs], choice };
};

/** Stable reorder putting sold keys first (their buyer is waiting on the original). */
export const soldFirst = <T>(items: T[], keyOf: (x: T) => string, sold: Set<string> | null | undefined): T[] => {
  if (!sold?.size) return items;
  const a: T[] = [];
  const b: T[] = [];
  for (const x of items) (sold.has(keyOf(x)) ? a : b).push(x);
  return a.length ? [...a, ...b] : items;
};
